import type { BadgeVariant } from '../components/symphony/ui/Badge';
import type { TaskStatusBadge } from './task-status';

// The goal status vocabulary for the Goals tab — the badge STYLE and the completion-ring arc color,
// keyed by the Goal entity's own status (KICKOFF / ACTIVE / ACHIEVED). Mirrors the native apps: the badge
// reuses the task styles (kickoff → waiting-on-you peach, active → in-progress blue, achieved → done
// green), and the ring arc uses the native `ring*` tokens. `labelKey` is an i18n key localized at the
// render site via t(badge.labelKey), like the task status badges. Like `taskStatusBadge` it hands out a
// STYLE and never a colour — the `--ds-badge-*` triple behind it belongs to the token layer.
//
// The LABELS are the task-status ones (`tasks.status.*` → "In progress" / "Done"), which is what the
// Figma goal badges read: the design deliberately speaks the task vocabulary, so one state reads the same
// word everywhere in the app — and reusing those keys means no parallel goal catalog to translate.
//
// KICKOFF is the ONE exception, and it wears the goal entity's own word. It used to read
// `tasks.status.actionNeeded` ("Action Needed"), which was survivable while KICKOFF was a rare state a
// maestro put a big goal into. It is not survivable now: `start` is the sole writer of ACTIVE, so
// `create`, `promote` and `kickoff` all land here and EVERY goal is born into KICKOFF. "Action Needed" on
// a goal the user created a second ago is an alarm about a state where nothing is wrong and nothing is
// owed — the goal is planned and not yet under way, which is what "Kickoff" says. It also put web out of
// step with both phones, which have always shown a third word again ("Waiting on you"); all three now
// say the same thing. The chip KEEPS the peach `waiting-on-you` style — the goal genuinely is waiting on
// the user's go, and only the word was wrong.
export const GOAL_STATUS_BADGE: Record<string, TaskStatusBadge> = {
  KICKOFF: { labelKey: 'goals.status.kickoff', variant: 'waiting-on-you' },
  ACTIVE: { labelKey: 'tasks.status.inProgress', variant: 'in-progress' },
  ACHIEVED: { labelKey: 'tasks.status.done', variant: 'done' },
};

// The completion-ring arc color per goal status — the native `ring*` tint (peach / blue / green) filled
// against the grey ring track. The `--sym-ring-*` roles are defined in styles/globals.css. Falls back to
// the active blue for any unmapped status.
const GOAL_RING_ARC: Record<string, string> = {
  KICKOFF: 'var(--sym-ring-kickoff)',
  ACTIVE: 'var(--sym-ring-active)',
  ACHIEVED: 'var(--sym-ring-achieved)',
};

// ── Outcome ────────────────────────────────────────────────────────────────────────────────────
//
// `status` says whether a goal is still being pursued; `outcome` says whether it WORKED, and the two
// are deliberately orthogonal on the entity (see the goal proto). A goal the user called off, or one
// a sibling overtook, settles ABANDONED — and one whose plan was only half delivered settles ACHIEVED
// — so a screen that reads `status` alone congratulates the user for a goal that was dropped. Whenever
// an outcome is recorded it is therefore the badge, and the plain "Done" chip is not reachable for it.
//
// The vocabulary is the existing badge STYLE set, no new Style and no colour:
//   REACHED         → `done`, the green win. The only outcome that keeps it.
//   PARTLY_REACHED  → `waiting-on-you`, the app's attention peach: it happened, but not whole.
//   OVERTAKEN / CALLED_OFF / DEADLINE_PASSED → `waiting-on-a-reply`, the DS's one neutral grey — the
//     same "terminal, but never green" treatment the retired (MOOT) task badge takes, and for the same
//     reason: these are endings where the outcome was not delivered, not failures to report in red.
export const GOAL_OUTCOME_BADGE: Record<string, TaskStatusBadge> = {
  REACHED: { labelKey: 'goals.outcome.reached', variant: 'done' },
  PARTLY_REACHED: { labelKey: 'goals.outcome.partlyReached', variant: 'waiting-on-you' },
  OVERTAKEN: { labelKey: 'goals.outcome.overtaken', variant: 'waiting-on-a-reply' },
  CALLED_OFF: { labelKey: 'goals.outcome.calledOff', variant: 'waiting-on-a-reply' },
  DEADLINE_PASSED: { labelKey: 'goals.outcome.deadlinePassed', variant: 'waiting-on-a-reply' },
};

// The ring arc that matches each outcome badge — same three readings, so the graph never says "win"
// over a badge that says "called off". The neutral three take Surface/Dark Gray rather than the
// light-gray neutral their badge fill uses, because the ring TRACK (`--sym-ring-track`) already IS
// Surface/Light Gray: an arc painted in it would be an invisible ring.
const GOAL_OUTCOME_RING_ARC: Record<string, string> = {
  REACHED: 'var(--sym-ring-achieved)',
  PARTLY_REACHED: 'var(--sym-ring-kickoff)',
  OVERTAKEN: 'var(--ds-surface-dark-gray)',
  CALLED_OFF: 'var(--ds-surface-dark-gray)',
  DEADLINE_PASSED: 'var(--ds-surface-dark-gray)',
};

/** The recorded outcome, when it names one of the five. `UNKNOWN_OUTCOME`, an empty string and a value
 *  this build does not know all read as "nobody has said" — never as a poor outcome. */
export function knownGoalOutcome(outcome: string | undefined): string | undefined {
  const value = outcome?.trim().toUpperCase();
  return value && value in GOAL_OUTCOME_BADGE ? value : undefined;
}

/** The status a goal READS as on screen: a plan whose every task is done reads ACHIEVED ("Done", green
 *  ring) whatever the stored entity status still says. Native does exactly this in both places that show a
 *  goal's status — `GoalRow` ("At 100% the whole badge becomes the Done badge … regardless of the server
 *  entity status") and `GoalScreen`'s progress line — because the server flips `status` on its own
 *  schedule, and a full ring beside an "In progress" badge is a contradiction the user sees before we do. */
export function effectiveGoalStatus(status: string, percent: number): string {
  return percent >= 100 ? 'ACHIEVED' : status;
}

export function goalStatusBadge(status: string): TaskStatusBadge {
  return GOAL_STATUS_BADGE[status] ?? GOAL_STATUS_BADGE.ACTIVE;
}

export function goalRingArc(status: string): string {
  return GOAL_RING_ARC[status] ?? GOAL_RING_ARC.ACTIVE;
}

/** The badge a goal WEARS: its recorded outcome when it has one, otherwise its on-screen status.
 *  The outcome wins over both the entity status AND the 100%→Done override, which is the whole point
 *  of the field — a goal that was called off at full progress must not read as a win. */
export function goalBadge(goal: { status: string; percent: number; outcome?: string }): TaskStatusBadge {
  const outcome = knownGoalOutcome(goal.outcome);
  return outcome ? GOAL_OUTCOME_BADGE[outcome] : goalStatusBadge(effectiveGoalStatus(goal.status, goal.percent));
}

/** The completion-ring arc colour matching `goalBadge`. */
export function goalArc(goal: { status: string; percent: number; outcome?: string }): string {
  const outcome = knownGoalOutcome(goal.outcome);
  return outcome ? GOAL_OUTCOME_RING_ARC[outcome] : goalRingArc(effectiveGoalStatus(goal.status, goal.percent));
}
